import { cleanS3Url } from '@bynder-studio/misc';
import type { IAssetsLoader } from '../../AssetLoader/IAssetsLoader';
import { ElementUpdateTypes } from '../../Enums/ElementUpdateTypes';
import { AudioAsset } from '../Assets/AudioAsset';
import type { IAsset } from '../Assets/IAsset';
import { type AudioElementParams, ElementUpdateOptions } from '../../types';
import { BaseTimelineElement } from './BaseTimelineElement';
import { AudioCompElement } from '../CompModels/Elements/AudioCompElement';
import { VirtualData } from '../Shared/VirtualData';
import { TimelineBehavior } from '../../Enums/TimelineBehavior';
import { PreviewTypes } from '../../Enums/PreviewTypes';
import type { CreativeTypes } from '../../Enums/CreativeTypes';

export class AudioElement extends BaseTimelineElement {
    isAssetLoading: boolean | undefined;

    src: string | null | undefined = null;

    srcId: string | null | undefined = null;

    srcType: string | null | undefined = null;

    offsetTime = 0;

    fileName: string | null = null;

    assetLoader: IAssetsLoader;

    gain = 0;

    fadeIn = 0;

    fadeOut = 0;

    timelineBehavior: TimelineBehavior = TimelineBehavior.AUTO;

    virtualData: VirtualData = { bynderCollectionId: null, allowPersonalUpload: false };

    constructor(params: AudioElementParams) {
        super();
        this.setProperties(params);
    }

    update(params: AudioElementParams, options?: ElementUpdateOptions): Set<ElementUpdateTypes> {
        const { frameRate = 25 } = options || {};
        const oldSrc = this.src;
        const oldOffsetTime = this.offsetTime;
        const oldDuration = this.duration;
        const oldStartFrame = this.startFrame;
        const updateTypes: Set<ElementUpdateTypes> = this.setProperties(params);

        if (
            this.src !== oldSrc ||
            this.offsetTime !== oldOffsetTime ||
            this.duration !== oldDuration ||
            this.startFrame !== oldStartFrame
        ) {
            this.constructAsset(frameRate);
        }

        return updateTypes;
    }

    setProperties(params: Partial<AudioElementParams>): Set<ElementUpdateTypes> {
        const updateTypes: Set<ElementUpdateTypes> = super.setProperties(params);

        if (params.src !== undefined) {
            this.src = params.src;
            updateTypes.add(ElementUpdateTypes.SOURCE);
        }

        if (params.srcId !== undefined) {
            this.srcId = params.srcId;
            updateTypes.add(ElementUpdateTypes.SOURCE);
        }

        if (params.srcType !== undefined) {
            this.srcType = params.srcType;
            updateTypes.add(ElementUpdateTypes.SOURCE);
        }

        if (params.offsetTime !== undefined) {
            this.offsetTime = params.offsetTime;
            updateTypes.add(ElementUpdateTypes.SOURCE);
        }

        if (params.fileName !== undefined) {
            this.fileName = params.fileName;
            updateTypes.add(ElementUpdateTypes.SOURCE);
        }

        if (params.gain !== undefined) {
            this.gain = params.gain;
            updateTypes.add(ElementUpdateTypes.AUDIO_GAIN);
        }

        if (params.fadeIn !== undefined) {
            this.fadeIn = params.fadeIn;
            updateTypes.add(ElementUpdateTypes.AUDIO_FADE_IN);
        }

        if (params.fadeOut !== undefined) {
            this.fadeOut = params.fadeOut;
            updateTypes.add(ElementUpdateTypes.AUDIO_FADE_OUT);
        }

        if (
            params.timelineBehavior !== undefined &&
            this.timelineBehavior !== params.timelineBehavior &&
            Object.values(TimelineBehavior).includes(params.timelineBehavior)
        ) {
            this.timelineBehavior = params.timelineBehavior || TimelineBehavior.AUTO;
            updateTypes.add(ElementUpdateTypes.TIMELINE_BEHAVIOR);
        }

        if (params.virtualData !== undefined) {
            this.virtualData = params.virtualData;
        }

        return updateTypes;
    }

    setAssetLoader(loader: IAssetsLoader): void {
        this.assetLoader = loader;
    }

    getAssetId(): string {
        return `${this.srcId}-${this.startFrame}-${this.offsetTime}-${this.duration}`;
    }

    constructAsset(frameRate = 25): void {
        if (!this.src) {
            return;
        }

        const asset = new AudioAsset({
            id: this.getAssetId(),
            src: this.src,
            startFrame: this.startFrame,
            offsetTime: this.offsetTime,
            duration: this.duration,
            frameRate,
        });

        this.isAssetLoading = true;
        this.assetLoader.setAsset(asset);
        this.assetLoader
            .loadAudio(asset)
            .catch((err) => {
                console.warn('Audio asset loading error. Url:', this.src, err);
            })
            .finally(() => {
                this.isAssetLoading = false;
            });
    }

    isContainsAsset(asset: IAsset): boolean {
        return asset instanceof AudioAsset && asset.id === this.srcId && cleanS3Url(asset.src) === cleanS3Url(this.src);
    }

    getCompElement(frameIndex: number) {
        const compElement = new AudioCompElement();

        compElement.id = `audio_element_${this.srcId}_${this.renderOrder}`;
        compElement.originalElement = this;
        compElement.isAssetLoading = this.isAssetLoading;
        compElement.assetId = this.getAssetId();
        compElement.gainValue = this.getGainValue();
        compElement.hidden = this.hidden;

        return compElement;
    }

    getGainValue(): number {
        return Math.pow(10, this.gain / 20);
    }

    getFadeScale(time: number, playbackDuration: number, frameRate: number): number {
        const duration = (this.duration - Math.max(0, this.startFrame + this.duration - playbackDuration)) / frameRate;
        const startAt = this.startFrame / frameRate;

        if (time < startAt || time >= startAt + duration) {
            return 0;
        }

        const fadeInScale = time >= startAt + this.fadeIn ? 1 : (time - startAt) / this.fadeIn;
        const fadeOutScale = time <= startAt + duration - this.fadeOut ? 1 : (startAt + duration - time) / this.fadeOut;

        return fadeInScale * fadeOutScale;
    }

    getValidationRules(creativeType: CreativeTypes, previewType: PreviewTypes) {
        const rules = super.getValidationRules(creativeType, previewType);

        if (previewType === PreviewTypes.CONTENT) {
            return rules;
        }

        return {
            ...rules,
            src: {
                REQUIRED: true,
                REQUIRED_MSG: 'This element is no longer available, please replace it to publish the design',
            },
        };
    }

    toObject(): AudioElementParams {
        const baseObject = super.toObject();

        return {
            ...baseObject,
            startFrame: this.startFrame,
            duration: this.duration,
            src: this.src,
            srcId: this.srcId,
            srcType: this.srcType,
            offsetTime: this.offsetTime,
            fileName: this.fileName,
            gain: this.gain,
            fadeIn: this.fadeIn,
            fadeOut: this.fadeOut,
            timelineBehavior: this.timelineBehavior,
            virtualData: this.virtualData,
        };
    }
}
